// @ts-ignore esModuleInterop
import React, { useMemo } from 'react'
import type { Listing, OnlineAuctionSegment } from '@adc/sdk/types'
import type {
  AuctionBlueprintOnlineReactiveArgs,
  AuctionBlueprintScheduledOnlineReactiveArgs,
} from '@adc/blueprint'
import { isAfter } from 'date-fns'
import * as graph from '@adc/sdk'
import { bidding } from '@adc/blueprint'
import {
  hooks,
  InfoPill,
  constants,
  Text,
  CounterOfferCountdown,
  HeaderCard,
  Icon,
  PeriodIndicator,
  StatusMessage,
  Stepper,
  utils,
} from '@adc/ui-adc'
import classnames from 'classnames/bind'
import moment from 'moment'
import styles from './index.scss'

const cx = classnames.bind(styles)

const Place: { [key: number]: string } = {
  1: '1st',
  2: '2nd',
  3: '3rd',
  4: '4th',
}

export type ActionableSealedBidStatus =
  | 'awaitingFirstOffer'
  | 'offerPlaced'
  | 'offerPlacedUpdateOffer'
  | 'counterOfferReceived' // case for ts-pattern match where we can make offerPlaced and counterOfferReceived mutually exclusive
  | 'rejectedOffer'

type ActionableSealedBidStatusActions = {
  updateOffer?: boolean
  withdrawOffer?: boolean
  guidedMakeAnOffer?: boolean
  declineCounterOffer?: boolean
  acceptCounterOffer?: boolean
  offerWon?: boolean
}

export type GeneralSealedBidStatus =
  | 'openOfferPeriod'
  | 'sellerReviewDateSet'
  | 'offersUnderReview'
  | 'highestAndBestOpen'
  | 'highestAndBestDecision'
  | 'offerAccepted'
  | 'pending'

export type GeneralSealedBidData = {
  status: GeneralSealedBidStatus
  additionalData?: any
  availableActions: ActionableSealedBidStatusActions
}

export type GeneralSealedBidOutput = {
  data: GeneralSealedBidData
  actions: object
}

export const isAcceptedOffer = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing'] | Listing
) => {
  return [
    graph.enums.ListingStatus.SaleGatekeeping,
    graph.enums.ListingStatus.SalePaymentPending,
    graph.enums.ListingStatus.InClosing,
    graph.enums.ListingStatus.Closed,
  ].includes(listing?.listing_status)
}

export const hasCurrentUserOfferAccepted = (
  segment:
    | AuctionBlueprintScheduledOnlineReactiveArgs['segment']
    | OnlineAuctionSegment
) =>
  segment?.current_user_bidding_status ===
  graph.enums.OnlineAuctionSegmentBiddingStatus.Won

// TODO: create a separate hook or util function which will take the segment status and spit out one
// of a flat list of options which will control variants, ideally this component will be built to handle a subset of those options.
// Other components may handle a different subset or all of the options. Something like a better version of what we have in common.ts for the bid banner before/after/during statuses
// Probably a hook like const { data, actions } = useSegmentSealedBidStatus where data contains the current users offer and one of the options i mentioned above which consolidates the status into something more manageable
// the actions might be things like a set of actions for each status, so if the status is offerPlaced, then the actions might be update or withdraw
export const useActionableSealedBidStatus = ({
  segment,
}: {
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
}): {
  data: {
    status: ActionableSealedBidStatus
    additionalData?: any
    availableActions: ActionableSealedBidStatusActions
  }
  actions: object // if we have actual functions to call as part of this, it should go in actions, but showing what independent functionality pieces of components are available to show is still part of data
} => {
  const bidRank = useMemo(
    () => segment.__methods__.getCurrentUserSealedBidRank(),
    [segment]
  )

  const bid = useMemo(
    () => segment.__methods__.getCurrentUserSealedBid(),
    [segment]
  )

  const isRejectedOffer = useMemo(
    () => segment.__methods__.isCurrentUserSealedBidRejected(),
    [segment]
  )

  // TODO: data and actions are a bit redundant right now, maybe we'll make componenet rendering more based on actions but then the content inside could be data dependent
  if (bidRank && !isRejectedOffer) {
    // branch here for counter offer situation
    if (segment.__methods__.getCounterofferBid(bid)) {
      return {
        data: {
          status: 'counterOfferReceived',
          availableActions: {
            declineCounterOffer: true,
            acceptCounterOffer: true,
            withdrawOffer: true,
          },
        },
        actions: {},
      }
    } else {
      return {
        data: {
          status: 'offerPlaced',
          availableActions: { updateOffer: true, withdrawOffer: true },
        },
        actions: {},
      }
    }
  } else {
    const awaitingFirstOfferActions: any = {
      data: {
        status: 'awaitingFirstOffer',
        availableActions: { guidedMakeAnOffer: true },
      },
      actions: {},
    }

    const rejectedActions: any = {
      data: {
        status: 'rejectedOffer',
        availableActions: {
          updateOffer: segment.__methods__.allowToSubmitNewOffers(),
        },
      },
      actions: {},
    }

    return isRejectedOffer ? rejectedActions : awaitingFirstOfferActions
  }
}

export const useGeneralizedSealedBidStatus = ({
  segment,
  listing,
}: {
  segment:
    | AuctionBlueprintScheduledOnlineReactiveArgs['segment']
    | OnlineAuctionSegment
  listing: AuctionBlueprintOnlineReactiveArgs['listing'] | Listing
}): GeneralSealedBidOutput => {
  const currentServerTime = bidding.hooks.experimental_useCurrentTime()

  if (isAcceptedOffer(listing)) {
    return {
      data: {
        status: 'offerAccepted',
        availableActions: {
          offerWon: hasCurrentUserOfferAccepted(segment),
        },
      },
      actions: {},
    }
  }

  if (
    segment?.segment_status ===
    graph.enums.OnlineAuctionSegmentStatus.SellerReviewPeriod
  ) {
    return {
      data: {
        status: 'offersUnderReview',
        availableActions: {},
        additionalData: {
          countDown: segment?.configuration?.review_date,
        },
      },
      actions: {},
    }
  }

  if (
    segment?.segment_status &&
    [graph.enums.OnlineAuctionSegmentStatus.HighestAndBestOpen].includes(
      segment?.segment_status
    )
  ) {
    return {
      data: {
        status: 'highestAndBestOpen',
        availableActions: {},
        additionalData: {
          countDown: segment.initial_end_date,
        },
      },
      actions: {},
    }
  }

  if (
    segment?.segment_status &&
    [graph.enums.OnlineAuctionSegmentStatus.HighestAndBestDecision].includes(
      segment?.segment_status
    )
  ) {
    return {
      data: {
        status: 'highestAndBestDecision',
        availableActions: {},
        additionalData: {
          countDown: segment.initial_end_date,
        },
      },
      actions: {},
    }
  }

  const isReviewDateInTheFuture =
    segment?.configuration?.review_date &&
    currentServerTime &&
    isAfter(
      new Date(segment?.configuration?.review_date),
      new Date(currentServerTime)
    )

  if (
    [
      graph.enums.ListingStatus.SalePending,
      graph.enums.ListingStatus.SalePendingCleared,
      graph.enums.ListingStatus.AuctionInProgress,
    ].includes(listing.listing_status) &&
    isReviewDateInTheFuture
  ) {
    return {
      data: {
        status: 'sellerReviewDateSet',
        availableActions: {},
        additionalData: {
          countDown: segment?.configuration?.review_date,
        },
      },
      actions: {},
    }
  }

  if (
    segment?.segment_status ===
      graph.enums.OnlineAuctionSegmentStatus.Pending ||
    listing.listing_status === graph.enums.ListingStatus.NoSale
  ) {
    return {
      data: {
        status: 'pending',
        availableActions: {},
      },
      actions: {},
    }
  }

  return {
    data: {
      status: 'openOfferPeriod',
      availableActions: {},
    },
    actions: {},
  }
}

const { ColorNames } = constants

/**
 * This WIP components covers:
 * 1. Counteroffer Expiration Countdown
 * 2. Current User Offer Ranking (1st, 2nd, 3rd place, etc)
 * 3. Update Offer CTA
 * 4. Guided Make an Offer Form CTA: Submit your offer to see where you are on the leaderboard
 *
 * It's intended to be passed into the OfferAmountHeader component as a prop.
 *
 * Extra thought, possibly de-couple the returning of the info pill from the logic of consuming the useActionableSealedBidStatus
 * data in case we want to have something to just pass in an info pill type for override situation where we just want to show a
 * certain one for a certain location.
 *
 * @returns
 */

interface SealedBidStatusInfoPillProps {
  override?: ActionableSealedBidStatus
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  listing: AuctionBlueprintOnlineReactiveArgs['listing'] | Listing
  isMobile?: boolean
}

export const SealedBidStatusInfoPill = ({
  override,
  segment,
  listing,
  isMobile,
}: SealedBidStatusInfoPillProps): React.JSX.Element => {
  const { data } = useActionableSealedBidStatus({ segment })

  const isRejectedOffer = useMemo(
    () => segment.__methods__.isCurrentUserSealedBidRejected(),
    [segment]
  )

  const bidRank = useMemo(
    () => segment.__methods__.getCurrentUserSealedBidRank(),
    [segment]
  )
  const counterOffer = bidRank?.bid?.negotiation_sequence?.latest_counter_bid

  const currentUserRankedOffer = segment?.bids_ranking?.ranking.find(
    bidRank => bidRank.belongs_to_current_user
  )

  const rankPlace: string = Place[currentUserRankedOffer?.rank ?? 1]

  if (isRejectedOffer) {
    return <></>
  }

  if (isAcceptedOffer(listing)) {
    if (isMobile && hasCurrentUserOfferAccepted(segment))
      return (
        <div className={cx('inline-flex')}>
          <InfoPill
            size='medium'
            backgroundColor={ColorNames.Transparent}
            foregroundColor={ColorNames.Success}
            iconName='check'
            label={
              <>
                <Text
                  data-elm-id='info_pill_accepted'
                  as='div'
                  weight='black'
                  size='big'
                  color={ColorNames.Success}
                >
                  Accepted
                </Text>
              </>
            }
          />
        </div>
      )
    return <></>
  }

  switch (override || data.status) {
    case 'awaitingFirstOffer':
      if (isMobile) return <></>
      return (
        <div className={cx('inline-flex')}>
          <InfoPill
            size='small'
            includeBorder
            backgroundColor={ColorNames.InfoExtraLight}
            foregroundColor={ColorNames.Info}
            iconName='ribbon'
            label={
              <Text size='small' color={ColorNames.Info}>
                Submit your offer to see your offer <strong>Ranking!</strong>
              </Text>
            }
          />
        </div>
      )
    case 'offerPlaced':
      if (isMobile)
        return (
          <div className={cx('inline-flex')}>
            <InfoPill
              size='medium'
              backgroundColor={ColorNames.Transparent}
              foregroundColor={ColorNames.Info}
              iconName='medal'
              label={
                <>
                  <Text
                    data-elm-id='info_pill_place_rank'
                    as='div'
                    weight='black'
                    size='big'
                    color={ColorNames.Info}
                  >
                    {`${
                      rankPlace || `${String(currentUserRankedOffer?.rank)}th`
                    } Place`}
                  </Text>
                </>
              }
            />
          </div>
        )
      return (
        <div className={cx('inline-flex')}>
          <InfoPill
            size='small'
            includeBorder
            backgroundColor={ColorNames.InfoExtraLight}
            foregroundColor={ColorNames.Info}
            iconName='check'
            label={
              <>
                <Text.Body as='div' size='small' color={ColorNames.Info}>
                  {'Your offer is ranked in '}
                  <b data-elm-id='info_pill_place_rank'>{`${
                    rankPlace || `${String(currentUserRankedOffer?.rank)}th`
                  }`}</b>{' '}
                  place
                </Text.Body>
              </>
            }
          />
        </div>
      )
    case 'counterOfferReceived':
      if (isMobile || !counterOffer) return <></>
      return <CounterOfferCountdown bid={counterOffer} />

    case 'offerPlacedUpdateOffer': // this is to support product requirements of showing static content for the update offer modal rather than the pill which would normally show bid rank, its only accessible through the override prop
      if (isMobile) return <></>
      return (
        <div className={cx('inline-flex')}>
          <InfoPill
            size='small'
            includeBorder
            backgroundColor={ColorNames.InfoExtraLight}
            foregroundColor={ColorNames.Info}
            iconName='medal'
            label={
              <Text size='small' color={ColorNames.Info}>
                Update your offer to see where you stand on the{' '}
                <strong>Leaderboard!</strong>
              </Text>
            }
          />
        </div>
      )
    default:
      if (isMobile) return <></>
      return (
        <InfoPill
          size='small'
          content='Default Switch Case, determine correct info status for this situation'
          includeBorder
          backgroundColor={ColorNames.SuccessLight}
          foregroundColor={ColorNames.Success}
          iconName='money-bill'
        />
      )
  }
}

interface SealedBidStatusBarProps {
  segment: AuctionBlueprintScheduledOnlineReactiveArgs['segment']
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  generalSealedBidData: GeneralSealedBidData
  override?: GeneralSealedBidStatus
  isMobile?: boolean
}

export const SealedBidStatusBar = ({
  segment,
  listing,
  override,
  generalSealedBidData,
  isMobile,
}: SealedBidStatusBarProps) => {
  const startDate = listing?.auction?.start_date

  const lower = isMobile
    ? null
    : () => (
        <div className={cx('flex-center')}>
          <Stepper
            steps={[
              {
                stepItem: 'Offer Window',
                completed: true,
                elmId: 'offer_window',
              },
              {
                stepItem: 'Contract & Closing',
                completed: false,
                elmId: 'contract_closing',
              },
            ]}
            activeStep={1}
            elmId='sealed-bid-status-bar-stepper'
            size='small'
          />
        </div>
      )

  switch (override || generalSealedBidData.status) {
    case 'pending':
      return <></>
    case 'openOfferPeriod':
      return (
        <div className={cx('offer-status-card')}>
          <HeaderCard
            slots={{
              left: () => (
                <div
                  className={cx('inline-flex')}
                  data-elm-id='offer_status_card_status'
                >
                  <Icon
                    size='big'
                    color='info'
                    name='hand-holding-dollar'
                    iconStyle='solid'
                  />
                  <Text size='huge' color={ColorNames.Info}>
                    Seller Accepting Offers
                  </Text>
                </div>
              ),
              right: isMobile
                ? null
                : () => (
                    <div>
                      <Text
                        size='small'
                        color={ColorNames.Gray}
                        elmId='offer_status_card_description'
                      >
                        <strong>
                          The seller is accepting offers—submit or update your
                          best offer now!
                        </strong>{' '}
                        Update as often as you like to climb the leaderboard and
                        improve your chances.
                      </Text>
                    </div>
                  ),
              lower,
            }}
            gap='medium'
            cardProps={{
              borderStyle: 'none',
              borderColor: ColorNames.Info,
              padding: isMobile ? 'small' : 'x-large',
              elmId: 'offer_status',
            }}
          />
        </div>
      )
    case 'sellerReviewDateSet':
      return (
        <div className={cx('offer-status-card')}>
          <HeaderCard
            slots={{
              left: () => (
                <div className={cx('column-flex')}>
                  <PeriodIndicator // TODO: fix these once we have test data for dates
                    color={ColorNames.Info}
                    title='Offer Review Date Set'
                    iconName='hand-holding-dollar'
                    // @ts-ignore fix DS types, should be a bid wider
                    startDate={startDate}
                    endDate={generalSealedBidData.additionalData?.countDown}
                    fluid={false}
                    subTitle='Reviewing Offers in: '
                    elmId='offer_status_card_status'
                  />
                </div>
              ),
              right: isMobile
                ? null
                : () => (
                    <div>
                      <Text
                        size='small'
                        color={ColorNames.Gray}
                        elmId='offer_status_card_description'
                      >
                        <strong>
                          Submit your best offer before it’s too late.
                        </strong>{' '}
                        The seller will review all offers at this time.
                        Decisions are typically made within 24 hours.
                      </Text>
                    </div>
                  ),
              lower,
            }}
            gap='medium'
            cardProps={{
              borderStyle: 'none',
              borderColor: ColorNames.Info,
              padding: 'x-large',
              elmId: 'offer_status',
            }}
          />
        </div>
      )
    case 'offersUnderReview':
    case 'highestAndBestDecision': {
      const isOnHightestAndBestDecisionPeriod =
        (override || generalSealedBidData.status) === 'highestAndBestDecision'
      const offerReviewColorTreatment = isOnHightestAndBestDecisionPeriod
        ? ColorNames.Purple
        : ColorNames.Caution
      const subText = isOnHightestAndBestDecisionPeriod
        ? 'The seller is reviewing all Highest & Best offers.'
        : 'The seller is reviewing offers.'

      return (
        <div className={cx('offer-status-card')}>
          <HeaderCard
            slots={{
              left: () => (
                <div className={cx('column-flex')}>
                  <PeriodIndicator
                    color={offerReviewColorTreatment}
                    title='Offers Under Review'
                    iconName='magnifying-glass-dollar'
                    // @ts-ignore fix DS types, should be a bid wider
                    startDate={startDate}
                    // @ts-ignore fix DS types, should be a bid wider
                    endDate={segment?.initial_end_date}
                    fluid={false}
                    subTitle='Round ending in: '
                    elmId='offer_status_card_status'
                  />
                </div>
              ),
              right: isMobile
                ? null
                : () => (
                    <div>
                      <Text
                        size='small'
                        color={ColorNames.Gray}
                        elmId='offer_status_card_description'
                      >
                        <strong>{subText}</strong> Stay by your phone for
                        updates, as decisions are typically made within 24
                        hours.
                      </Text>
                    </div>
                  ),
              lower,
            }}
            gap='medium'
            cardProps={{
              borderStyle: 'gradient',
              borderColor: offerReviewColorTreatment,
              padding: 'x-large',
              elmId: 'offer_status',
            }}
          />
        </div>
      )
    }
    case 'highestAndBestOpen':
      return (
        <div className={cx('offer-status-card')}>
          <HeaderCard
            slots={{
              left: () => (
                <div className={cx('column-flex')}>
                  <PeriodIndicator
                    color={ColorNames.Purple}
                    title='Highest & Best Round'
                    iconName='bolt-lightning'
                    // @ts-ignore fix DS types, should be a bid wider
                    startDate={startDate}
                    // @ts-ignore fix DS types, should be a bid wider
                    endDate={segment?.initial_end_date}
                    fluid={false}
                    subTitle='Round ending in: '
                    elmId='offer_status_card_status'
                  />
                </div>
              ),
              right: isMobile
                ? null
                : () => (
                    <div>
                      <Text
                        size='small'
                        color={ColorNames.Gray}
                        elmId='offer_status_card_description'
                      >
                        <strong>The Highest & Best Round is open!</strong> You
                        have 24 hours to submit your best offer and increase
                        your chances of winning this property.
                      </Text>
                    </div>
                  ),
              lower,
            }}
            gap='medium'
            cardProps={{
              borderStyle: 'gradient',
              borderColor: ColorNames.Purple,
              padding: 'x-large',
              elmId: 'offer_status',
            }}
          />
        </div>
      )
    case 'offerAccepted': {
      const hasCurrentUserWon = hasCurrentUserOfferAccepted(segment)
      const currentUserColorTreatment = hasCurrentUserWon
        ? ColorNames.AdcDarkGreen
        : ColorNames.Gray
      const header = hasCurrentUserWon
        ? 'Offer Accepted'
        : 'Seller Accepted Another Offer'
      const subText = hasCurrentUserWon
        ? 'Congratulations! The seller has accepted your offer.'
        : 'The seller has accepted another offer.'
      const offerInfo = hasCurrentUserWon
        ? 'Start contracting now.'
        : 'The property is now in contracting.'

      return (
        <div className={cx('offer-status-card')}>
          <HeaderCard
            slots={{
              left: () => (
                <div
                  className={cx('inline-flex')}
                  data-elm-id='offer_status_card_status'
                >
                  <Icon
                    size='big'
                    color={currentUserColorTreatment}
                    name='money-check-dollar-pen'
                    iconStyle='solid'
                  />
                  <Text size='huge' color={currentUserColorTreatment}>
                    {header}
                  </Text>
                </div>
              ),
              right: () => (
                <div>
                  <Text
                    size='small'
                    color={ColorNames.Gray}
                    elmId='offer_status_card_description'
                  >
                    <strong>{subText}</strong> {offerInfo}
                  </Text>
                </div>
              ),
              lower,
            }}
            gap='medium'
            cardProps={{
              borderStyle: 'gradient',
              borderColor: currentUserColorTreatment,
              padding: 'x-large',
              elmId: 'offer_status',
            }}
          />
        </div>
      )
    }
  }
}
interface SealedBidStatusInfoProps {
  generalSealedBidData: GeneralSealedBidData
  currentServerDateTime: moment.Moment
  override?: GeneralSealedBidStatus
}

export const SealedBidStatusInfo = ({
  generalSealedBidData,
  currentServerDateTime,
  override,
}: SealedBidStatusInfoProps) => {
  switch (override || generalSealedBidData.status) {
    case 'pending':
      return (
        <StatusMessage
          color={ColorNames.Info}
          title='Listing is Pending'
          description='Save this property for updates'
        />
      )
    case 'openOfferPeriod':
      return (
        <StatusMessage
          color={ColorNames.Info}
          title='Seller Accepting Offers'
          description='Submit your offer directly to the seller now'
          tooltipText='The seller is accepting offers — submit your offer now!  Update as often as you like to improve your chances.'
        />
      )
    case 'sellerReviewDateSet':
      // eslint-disable-next-line no-case-declarations
      const endTime = useMemo(
        () => moment(generalSealedBidData?.additionalData?.countDown),
        [generalSealedBidData?.additionalData?.countDown]
      )

      // @ts-ignore TODO: useCountdown hook should be better typed
      // eslint-disable-next-line no-case-declarations
      const { secondsRemaining } = endTime
        ? hooks.useCountdown({
            // @ts-ignore TODO: useCountdown hook should be better typed
            endTime,
            updateInterval: 1000,
            currentServerTime: currentServerDateTime,
          })
        : { secondsRemaining: undefined }
      return (
        <StatusMessage
          color={ColorNames.Info}
          title='Offer Review Date Set'
          description='Seller will review offers at this time'
          tooltipText='The seller will review all offers at this time, and decisions are typically made within 24 hours. Submit your offer now! The seller can accept an offer at any time.'
          badgeText={
            secondsRemaining > 0
              ? utils.countdown.formatCountdownTimeWithoutText(secondsRemaining)
              : undefined
          }
        />
      )
    case 'offersUnderReview':
      return (
        <StatusMessage
          color={ColorNames.Caution}
          title='Offers Under Review'
          description='Submit your offer to the seller before it is too late'
          tooltipText='The seller is reviewing offers. Submit an offer now, decisions are typically made within 24 hours.'
        />
      )
    case 'highestAndBestOpen':
    case 'highestAndBestDecision':
      return (
        <StatusMessage
          color={ColorNames.Purple}
          title='Highest & Best'
          description='Property is in Highest & Best'
          tooltipText='The seller has initiated a Highest & Best round. Buyers with active offers have 24 hours to update their offer to increase their chances of winning.'
        />
      )
    case 'offerAccepted':
      return (
        <StatusMessage
          color={ColorNames.Success}
          title='Sale Pending'
          description='Seller has accepted an offer'
        />
      )
  }
}
